import React, { useEffect, useRef } from 'react';
import Hotkeys from 'react-hot-keys';
import { Form, Formik } from 'formik';
import clsx from 'clsx';
import ReCAPTCHA from 'react-google-recaptcha';

import { Grid, TextField, Typography } from '@material-ui/core';
import RenderIf from '@components/RenderIf';

import { getLocalStorage, isEmptyObject, setLocalStorage } from '@utils/index';
import useStyles from './styles';
import { StepsProps } from '@components/VideoScriptGenerator/types';
import FooterSteps from '@components/VideoScriptGenerator/Steps/FooterSteps';
import { ValuesScriptData } from '@components/VideoScriptGenerator/Steps/StepsData';
import { EnvConfig } from '@pages/video-script-generator';

interface TextFieldStepProps extends StepsProps {
  data: ValuesScriptData;
  mapTitle?: () => string;
  values: any;
  useCaptcha?: boolean;
  displayError?: boolean;
  env?: EnvConfig;
}

const TextFieldStep = ({
  next,
  back,
  position,
  data,
  mapTitle,
  values,
  useCaptcha = false,
  displayError,
  env
}: TextFieldStepProps) => {
  const classes = useStyles();
  const inputRef = useRef<null | HTMLTextAreaElement>(null);

  // TBD: This value is any for now, because is implemented for 
  // multiple value types depending on the steps
  const onSubmit = async (value: any) => {
    if (data.valueName === 'productWebsite' && !value.productWebsite.includes('http')) {
      value.productWebsite = `https://${value.productWebsite}`
    }
    
    if (data.populate) {
      setLocalStorage(data.valueName, value[data.valueName]);
    }

    next({
      ...value,
    });
  };

  const handleOnTokenChange = (setFieldValue: { (field: string, value: any, shouldValidate?: boolean | undefined): void; (arg0: string, arg1: any): void; }, value: string | null) => {
    setFieldValue('g-recaptcha-response', value);
  };

  const getInitValue = () => {
    let initValue = values[data.valueName] || '';
    if (data.populate) {
      const populate = getLocalStorage(data.valueName);
      initValue = populate || initValue;
    }
    return initValue;
  };

  const avoidNewLine = (e: React.KeyboardEvent<HTMLDivElement>, handleSubmit: { (e?: React.FormEvent<HTMLFormElement> | undefined): void; (): void; }) => {
    if ((e.charCode == 13 || e.key === 'Enter') && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFocus = (event: any) => event.target.select();

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
      setTimeout(() => {
        inputRef?.current?.select();
      }, 100)
    }
  }, [data]);

  return (
    <Formik
      initialValues={{ [data.valueName]: getInitValue() }}
      validationSchema={data.schemeValidation}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        handleChange,
        touched,
        setFieldValue,
        handleSubmit,
      }) => (
        <Form className={classes.form}>
          <Grid
            container
            className={classes.textFieldContainer}
            direction="column"
            justify="center"
            alignItems="flex-start"
          >
            <Typography className={classes.title} variant="h2">
              {mapTitle ? mapTitle() : data.title}
            </Typography>
            <TextField
              inputRef={inputRef} 
              className={clsx(classes.textField, {
                [classes.textFieldError]:
                  errors[data.valueName] && touched[data.valueName],
              })}
              id={data.valueName}
              value={values[data.valueName] || ''}
              placeholder={data.placeholder}
              multiline
              onKeyPress={(e) => {
                avoidNewLine(e, handleSubmit);
              }}
              onChange={handleChange}
              onFocus={handleFocus}
            />
            <RenderIf condition={(!!errors[data.valueName] && displayError) || false}>
              <Typography className={classes.error}>
                <>
                  * {errors[data.valueName]}
                </>
              </Typography>
            </RenderIf>

            <RenderIf condition={useCaptcha}>
              <Grid container alignItems="center" justify="center">
                <ReCAPTCHA
                  sitekey={env?.recaptchaSiteKeyChallenge || ''}
                  onChange={(value) =>
                    handleOnTokenChange(setFieldValue, value)
                  }
                />
                <input
                  name="g-recaptcha-response"
                  value={values['g-recaptcha-response'] || ''}
                  hidden
                  onChange={handleChange}
                />
              </Grid>
            </RenderIf>
            <Hotkeys key="next" keyName="enter" onKeyUp={() => handleSubmit()}>
              <FooterSteps
                back={back}
                position={position}
                available={!isEmptyObject(errors)}
              />
            </Hotkeys>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default TextFieldStep;
